import React, { useEffect, useId, useRef } from 'react';

/**
 * BottomSheet — mobile-first dialog primitive.
 *
 * Mobile (≤ sm): slides up from bottom, rounded-top-3xl, max 85vh.
 * Desktop (≥ md): centered modal, rounded-2xl, max-w-md, max 80vh.
 *
 * Behaviour:
 *  - Backdrop click closes
 *  - Escape key closes
 *  - Body scroll locked while open
 *  - Focus moved to first focusable on open
 *  - Focus restored to previous element on close
 *  - aria-modal + aria-labelledby for screen readers
 *  - Safe-area-inset padding for iPhone home indicator
 */

export interface BottomSheetProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: React.ReactNode;
  /** Optional sticky footer slot for action buttons */
  footer?: React.ReactNode;
  /** Set false to hide the close (×) button — e.g. for required confirmations */
  showClose?: boolean;
}

const BottomSheet: React.FC<BottomSheetProps> = ({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  showClose = true,
}) => {
  const sheetRef = useRef<HTMLDivElement>(null);
  const prevFocusRef = useRef<HTMLElement | null>(null);
  const titleId = useId();

  useEffect(() => {
    if (!open) return;

    prevFocusRef.current = document.activeElement as HTMLElement | null;

    // Focus the first focusable element inside the sheet
    const focusable = sheetRef.current?.querySelector<HTMLElement>(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    );
    focusable?.focus();

    // Escape handler
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);

    // Lock body scroll
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      prevFocusRef.current?.focus();
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end md:items-center justify-center"
      // Prevent backdrop scroll bleed-through on iOS
      onTouchMove={e => {
        if (e.target === e.currentTarget) e.preventDefault();
      }}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        aria-hidden="true"
      />
      {/* Sheet */}
      <div
        ref={sheetRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={[
          'relative w-full md:max-w-md bg-white shadow-2xl',
          'rounded-t-3xl md:rounded-2xl',
          'max-h-[85vh] md:max-h-[80vh] flex flex-col',
          'pb-[max(env(safe-area-inset-bottom),0px)]',
        ].join(' ')}
      >
        {/* Drag handle (mobile only — visual affordance) */}
        <div className="flex justify-center pt-2 md:hidden">
          <div className="w-12 h-1 rounded-full bg-stone-300" aria-hidden="true" />
        </div>

        {/* Header */}
        <div className="px-5 pt-4 pb-3 border-b border-stone-100 relative shrink-0">
          <h2
            id={titleId}
            className="text-xl font-semibold text-stone-900 pr-12 tracking-tight"
          >
            {title}
          </h2>
          {description && (
            <p className="text-sm text-stone-600 mt-1 leading-relaxed pr-12">
              {description}
            </p>
          )}
          {showClose && (
            <button
              onClick={onClose}
              className="absolute top-3 right-3 h-11 w-11 flex items-center justify-center text-stone-500 hover:text-stone-900 rounded-full hover:bg-stone-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-stone-500"
              aria-label="Close"
              type="button"
            >
              <span
                className="material-symbols-rounded"
                style={{ fontSize: 24 }}
                aria-hidden="true"
              >
                close
              </span>
            </button>
          )}
        </div>

        {/* Body (scrolls) */}
        <div className="flex-1 overflow-y-auto">{children}</div>

        {/* Footer (sticky at bottom of sheet) */}
        {footer && (
          <div className="border-t border-stone-200 px-5 py-4 shrink-0 bg-white">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

export default BottomSheet;
